import type { ImagingInstance } from "$global-providers/rxdb-provider/collections/imaging-instance";
import { CineStatus } from "elephant-web/gql";

export type CineAvailability = "READY" | "PENDING" | "FAILED" | "UNAVAILABLE";

export type CineMetadata = {
  numberOfFrames: number;
  frameRate: number | null;
  durationMs: number | null;
  sizeBytes: number | null;
  availability: CineAvailability;
};

export type CineFields = Pick<
  ImagingInstance,
  | "numberOfFrames"
  | "frameRate"
  | "cineStatus"
  | "cineS3Key"
  | "cineDurationMs"
  | "cineSizeBytes"
>;

const toAvailability = (instance: CineFields): CineAvailability => {
  switch (instance.cineStatus) {
    case CineStatus.Ready:
      return "READY";
    case CineStatus.Pending:
      return "PENDING";
    case CineStatus.Failed:
      return "FAILED";
    default:
      return instance.cineS3Key ? "READY" : "UNAVAILABLE";
  }
};

export const toCineMetadata = (instance: CineFields): CineMetadata | null => {
  const numberOfFrames = instance.numberOfFrames ?? 0;
  if (numberOfFrames <= 1) return null;

  return {
    numberOfFrames,
    frameRate: instance.frameRate ?? null,
    durationMs: instance.cineDurationMs ?? null,
    sizeBytes: instance.cineSizeBytes ?? null,
    availability: toAvailability(instance),
  };
};

export const cineFrameRate = (
  metadata: CineMetadata,
  videoDurationSeconds?: number,
): number | null => {
  if (videoDurationSeconds && Number.isFinite(videoDurationSeconds)) {
    return metadata.numberOfFrames / videoDurationSeconds;
  }
  if (metadata.frameRate) return metadata.frameRate;
  if (metadata.durationMs) {
    return metadata.numberOfFrames / (metadata.durationMs / 1000);
  }
  return null;
};

export const describeCine = (metadata: CineMetadata): string => {
  const frames = `${metadata.numberOfFrames}-frame loop`;
  switch (metadata.availability) {
    case "READY":
      return frames;
    case "PENDING":
      return `${frames}, still processing`;
    case "FAILED":
      return `${frames}, failed to process`;
    default:
      return `${frames}, unavailable`;
  }
};
